import { toast } from 'sonner'

type ToastVariant = 'success' | 'error' | 'warning' | 'info'

interface ToastConfig {
  /** Secondary line under the message. */
  description?: string
  duration?: number
  action?: {
    label: string
    onClick: () => void
  }
}

const DEFAULT_DURATION = 3000

/** App-wide notifications, rendered by the single sonner <Toaster> mounted in App. */
class ToastService {
  private static instance: ToastService
  private defaultConfig: ToastConfig = { duration: DEFAULT_DURATION }

  private constructor() {
    // Private constructor for singleton
  }

  public static getInstance(): ToastService {
    if (!ToastService.instance) {
      ToastService.instance = new ToastService()
    }
    return ToastService.instance
  }

  /**
   * Set default configuration for all toasts
   */
  public setDefaultConfig(config: Partial<ToastConfig>): void {
    this.defaultConfig = { ...this.defaultConfig, ...config }
  }

  private show(variant: ToastVariant, message: string, config?: ToastConfig): string {
    const { action, ...rest } = { ...this.defaultConfig, ...config }
    const id = toast[variant](message, {
      ...rest,
      action: action ? { label: action.label, onClick: action.onClick } : undefined,
    })
    return String(id)
  }

  public success(message: string, config?: ToastConfig): string {
    return this.show('success', message, config)
  }

  public error(message: string, config?: ToastConfig): string {
    return this.show('error', message, config)
  }

  public warning(message: string, config?: ToastConfig): string {
    return this.show('warning', message, config)
  }

  public info(message: string, config?: ToastConfig): string {
    return this.show('info', message, config)
  }

  /**
   * Dismiss a toast by its ID
   */
  public dismiss(toastId?: string): void {
    toast.dismiss(toastId)
  }

  /**
   * Dismiss all toasts
   */
  public dismissAll(): void {
    toast.dismiss()
  }
}

const Toast = ToastService.getInstance()

export { ToastService, Toast }
